import { Paperclip, X } from "lucide-react";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import Loader from "../loader";
import { Controller } from "react-hook-form";
import TextInput from "./TextInput";
import toast from "react-hot-toast";
import { allowedImagesTypes, MAX_SIZE_IN_BYTES } from "../../constants/common";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";

interface FileUploadInputProps {
  name: string;
  label: string;
  accept: string;
  Tooltip?: ReactNode;
  control: any;
  uploadRef?: React.MutableRefObject<HTMLInputElement | null>;
  disabled?: boolean;
  fileName?: string;
  setFileName?: React.Dispatch<React.SetStateAction<string>>;
  setFileType?: React.Dispatch<React.SetStateAction<string>>;
  isAttachments?: number;
  handleAttachmentView?: (type: number) => Promise<void>;
  selectedType?: number;
  isGetFileByTypeLoading?: boolean;
  error?: any;
  attachmentType?: any;
  allowedTypes: string[];
  required?: boolean;
  previewFiles?: Record<
    number,
    {
      url: string;
      contentType: string;
    }
  >;
  setPreviewFiles?: Dispatch<
    SetStateAction<{
      [key: number]: {
        url: string;
        contentType: string;
      };
    }>
  >;
  onFileUpload?: (file: File | null) => void;
  onRemoveAttachment?: (info: {
    S_FileFieldName: string;
    s_FiledFileType: string;
  }) => void;
  S_FileFieldName?: string;
  s_FiledFileType?: string;

  isRemovable?: boolean;
}

const FileUploadInput = ({
  name,
  label,
  Tooltip,
  control,
  accept,
  uploadRef,
  disabled = false,
  fileName,
  setFileName,
  setFileType,
  isAttachments,
  handleAttachmentView,
  selectedType,
  isGetFileByTypeLoading,
  error,
  attachmentType,
  allowedTypes,
  required = true,
  previewFiles,
  setPreviewFiles,
  onFileUpload,
  onRemoveAttachment,
  S_FileFieldName,
  s_FiledFileType,
  isRemovable = true,
}: FileUploadInputProps) => {
  const { t, i18n } = useTranslation();
  const [isRemovedFromServer, setIsRemovedFromServer] = useState(false);

  useEffect(() => {
    setIsRemovedFromServer(false);
  }, [isAttachments]);

  const handleAttachment = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (...event: any[]) => void
  ) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          t(
            allowedTypes === allowedImagesTypes
              ? t("invalidFileTypeImage")
              : t("invalidFileTypeDocument")
          )
        );
        target.value = "";
        return;
      }

      if (file.size > MAX_SIZE_IN_BYTES) {
        toast.error(t("fileSizeExceedsLimit"));
        target.value = "";
        return;
      }

      onChange(file);
      setFileName?.(file.name);
      setFileType?.(file.type);
      onFileUpload?.(file);

      const localUrl = URL.createObjectURL(file);
      setPreviewFiles?.((prev) => ({
        ...prev,
        [String(attachmentType)]: {
          url: localUrl,
          contentType: file.type,
        },
      }));

      setIsRemovedFromServer(false);
    } else {
      onFileUpload?.(null);
    }
  };

  return (
    <div className="relative flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2 mt-1">
        <label className={`text-[13px] font-normal`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        {Tooltip}
      </div>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange } }) => (
          <>
            <div className="relative flex h-9 w-full border-b-2 border-input bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#8D8D87] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 input-fields">
              <TextInput
                type="file"
                accept={accept}
                className="hidden"
                ref={uploadRef}
                onChange={(e) => handleAttachment(e, onChange)}
              />

              <div className="overflow-hidden">
                <div className="text-nowrap">
                  {fileName || t("noFileSelected")}
                </div>

                <div
                  className={cn(
                    "absolute bottom-1 flex justify-center items-center gap-2 border border-black px-3 py-2 rounded-full bg-white",
                    i18n.language === "en" ? "right-0" : "left-0",
                    disabled ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                  onClick={() => !disabled && uploadRef?.current?.click()}
                >
                  <Paperclip size="15px" />
                  {t("uploadFile")}
                </div>
              </div>
            </div>

            {(previewFiles && attachmentType !== undefined && previewFiles[attachmentType]) ||
            (!isRemovedFromServer && isAttachments) ? (
              <div className="flex items-center justify-between">
                <p
                  onClick={() =>
                    attachmentType !== undefined &&
                    handleAttachmentView?.(attachmentType)
                  }
                  className="text-xs text-blue-600 underline cursor-pointer"
                >
                  {t("viewAttachment")}
                </p>
                <div className="flex items-center gap-1">
                  {selectedType === attachmentType && isGetFileByTypeLoading && (
                    <Loader size={14} />
                  )}
                  {isRemovable && !disabled && (
                    <button
                      type="button"
                      onClick={() => {
                        if (uploadRef?.current) {
                          uploadRef.current.value = "";
                        }
                        setFileName?.("");
                        setFileType?.("");
                        onChange(null);

                        if (attachmentType !== undefined) {
                          setPreviewFiles?.((prev) => {
                            const next = { ...prev };
                            delete next[attachmentType];
                            return next;
                          });
                        }

                        if (!isRemovedFromServer && isAttachments) {
                          setIsRemovedFromServer(true);
                          if (S_FileFieldName && s_FiledFileType) {
                            onRemoveAttachment?.({ S_FileFieldName, s_FiledFileType });
                          }
                        }
                      }}
                      className="ml-1"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      />
      {error && (
        <p className="text-red-600 italic text-[10px] absolute -bottom-[15px]">
          {error.message}
        </p>
      )}
    </div>
  );
};

export default React.memo(FileUploadInput);
